import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Plus, Trash2, MapPin, Calendar, Clock, Sparkles, Lock,
  Check, GripVertical, Search, User, Building2, ExternalLink, Play,
  Phone, MessageSquare, CheckCircle2, ChevronDown, ChevronRight, Package,
  Globe, Printer, AlertTriangle, Loader2, FileText, Mail, Link as LinkIcon,
  Download, MessageSquareText,
} from "lucide-react";
import api from "../lib/api";
import { EntityShell } from "../components/shell";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PlaceInput from "../components/PlaceInput";
import HidroFeedbackBar from "../components/HidroFeedbackBar";
import HVDateInput from "../components/HVDateInput";
import HidroMarkdown from "../components/HidroMarkdown";
import VoiceTextarea from "../components/VoiceTextarea";
import { printInLightMode } from "../lib/printInLightMode";
import useServerPdf from "../lib/useServerPdf";
import { uploadUrl } from "../lib/uploads";
import { DeepModePill, DeepItineraryPanel, DebriefModal } from "../components/trip/DeepItinerary";
import TripRouteMap from "../components/TripRouteMap";
import { useBranding } from "../context/TenantBrandingContext";

// Wave 25.17 — Google Maps Embed iframe retired; TripRouteMap (Leaflet
// + OSRM) is now the renderer. The "Open in Google Maps" deep links
// remain free and require no API key.

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [trip, setTrip] = useState(null);
  // Active immersive tab — locked 2026-02 #67. Persisted via URL hash so
  // browser-back works the way the user expects.
  const [tab, setTab] = useState(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    return ["plan", "map", "calendar", "insights", "itinerary"].includes(h) ? h : "plan";
  });
  useEffect(() => {
    const want = `#${tab}`;
    if (window.location.hash !== want) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${want}`);
    }
  }, [tab]);
  // Snapshot of the trip when it was last loaded/saved — used by `save()`
  // to detect which existing meetings would be rescheduled by the new
  // arrival_times so we can prompt the user before silently mutating
  // their calendar.
  const originalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [genBriefFor, setGenBriefFor] = useState(null); // stop id currently generating
  const [err, setErr] = useState("");
  const [driveTimes, setDriveTimes] = useState(null);
  const [loadingDriveTimes, setLoadingDriveTimes] = useState(false);
  // Per-stop expand/collapse state, keyed by stop.id; default = expanded
  const [collapsed, setCollapsed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`trip-collapsed-${id}`) || "[]")); }
    catch { return new Set(); }
  });
  const persistCollapsed = (next) => {
    try { localStorage.setItem(`trip-collapsed-${id}`, JSON.stringify([...next])); } catch (_e) {}
  };
  const toggleCollapsed = (sid) => {
    setCollapsed((prev) => { const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); persistCollapsed(n); return n; });
  };
  const collapseAll = () => {
    const n = new Set((trip?.stops || []).map((s) => s.id));
    setCollapsed(n); persistCollapsed(n);
  };
  const expandAll = () => { setCollapsed(new Set()); persistCollapsed(new Set()); };

  // Auto-resolve stops to existing leads/customers/suspects/briefs/meetings.
  // The endpoint auto-applies HIGH-CONFIDENCE matches (score ≥ 0.85 AND a
  // clear winner over the runner-up). Lower-confidence suggestions are
  // exposed in `linkSuggestions[stop.id]` so the user can pick from a
  // dropdown on each stop card.
  const [linkSuggestions, setLinkSuggestions] = useState({}); // { [stop_id]: suggestion }
  const [resolvingStops, setResolvingStops] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [debriefOpen, setDebriefOpen] = useState(false);
  const resolveStops = async () => {
    if (!trip?.id) return;
    setResolvingStops(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/resolve-stops`);
      const map = {};
      for (const sug of (data.suggestions || [])) map[sug.stop_id] = sug;
      setLinkSuggestions(map);
      if ((data.applied_count || 0) > 0) {
        // Server persisted lead_id/customer_id on auto-applied stops — refetch
        // so the UI shows the live state (link chips, enriched address, etc.).
        const fresh = await api.get(`/trips/${trip.id}`);
        setTrip(fresh.data);
        originalRef.current = fresh.data;
      }
      setErr("");
      // Surface a tiny inline toast-style banner via err for now (success-tinted).
      const txt = (data.applied_count || 0) > 0
        ? `Auto-linked ${data.applied_count} stop${data.applied_count === 1 ? "" : "s"}.`
        : "No high-confidence matches — pick a link manually below.";
      setLinkResolveMsg(txt);
      setTimeout(() => setLinkResolveMsg(""), 4000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to resolve stops.");
    } finally {
      setResolvingStops(false);
    }
  };
  const [linkResolveMsg, setLinkResolveMsg] = useState("");

  // Manually link a stop to a specific entity (lead / customer / suspect).
  const linkStop = async (stopId, body) => {
    if (!trip?.id) return;
    try {
      const { data } = await api.post(`/trips/${trip.id}/stops/${stopId}/link`, body);
      // Patch the trip state with the freshly enriched stop
      setTrip((t) => ({
        ...t,
        stops: (t.stops || []).map((s) => (s.id === stopId ? { ...s, ...(data.stop || {}) } : s)),
      }));
      // Drop any suggestions for this stop — it's now linked
      setLinkSuggestions((prev) => { const n = { ...prev }; delete n[stopId]; return n; });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to link stop.");
    }
  };

  // Trip-level checklist helpers
  const addChecklistItem = (text) => {
    const item = { id: (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)), text, done: false };
    setTrip((t) => ({ ...t, checklist: [...(t.checklist || []), item] }));
  };
  const toggleChecklistItem = (cid) => {
    setTrip((t) => ({ ...t, checklist: (t.checklist || []).map((c) => c.id === cid ? { ...c, done: !c.done } : c) }));
  };
  const removeChecklistItem = (cid) => {
    setTrip((t) => ({ ...t, checklist: (t.checklist || []).filter((c) => c.id !== cid) }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
      originalRef.current = JSON.parse(JSON.stringify(data));
    } catch (_e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const fetchDriveTimes = async () => {
    setLoadingDriveTimes(true);
    try {
      const { data } = await api.get(`/trips/${id}/drive-times`);
      setDriveTimes(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not fetch drive times");
    }
    setLoadingDriveTimes(false);
  };

  // Auto-cascade arrival times whenever departure or drive-times change.
  // We push the recalculated arrival_time INTO trip.stops so the editor,
  // summary card, and PUT payload all see the same authoritative values.
  useEffect(() => {
    if (!trip || !trip.start_date || !trip.start_time || (trip.stops || []).length === 0) return;
    const next = _cascadeArrivalTimes({
      start_date: trip.start_date,
      start_time: trip.start_time,
      stops: trip.stops,
      drive_legs: driveTimes?.legs || null,
    });
    // Only update if anything actually changed (avoid render loop).
    const changed = next.some((s, i) => s.arrival_time !== trip.stops[i]?.arrival_time);
    if (changed) setTrip((t) => ({ ...t, stops: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trip?.start_date,
    trip?.start_time,
    driveTimes,
    // duration changes need to retrigger
    JSON.stringify((trip?.stops || []).map((s) => [s.id, s.duration_min, s.manual_travel_min, s.manual_arrival_time])),
  ]);

  // Trip is "soft-locked" once active or completed — users can still
  // edit arrival times + manual_travel_min on an ACTIVE trip so the
  // cascade-recalculate + reschedule-confirm flow is reachable. Other
  // structural changes (add/remove stops, edit address) stay locked.
  const isLocked = trip?.status === "active" || trip?.status === "completed";
  const arrivalEditable = trip?.status !== "completed";  // active OR draft

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      // Detect meeting reschedules (existing trip-linked meetings whose
      // arrival_time changed) — explicit confirm before silently mutating.
      if (originalRef.current && (originalRef.current.stops || []).length > 0) {
        const origById = new Map((originalRef.current.stops || []).map((s) => [s.id, s]));
        const reschedules = (trip.stops || []).filter((s) => {
          const o = origById.get(s.id);
          if (!o) return false;
          const oTime = (o.arrival_time || "").trim();
          const nTime = (s.arrival_time || "").trim();
          const oDur = Number(o.duration_min || 60);
          const nDur = Number(s.duration_min || 60);
          return o.meeting_id && (oTime !== nTime || oDur !== nDur);
        });
        if (reschedules.length > 0) {
          const list = reschedules.map((s) => `• ${s.title || "(stop)"} → ${s.arrival_time ? new Date(s.arrival_time).toLocaleString() : "(unset)"}`).join("\n");
          const ok = window.confirm(
            `${reschedules.length} calendar meeting${reschedules.length === 1 ? "" : "s"} will be rescheduled when you re-activate this trip:\n\n${list}\n\nContinue?`
          );
          if (!ok) { setSaving(false); return; }
        }
      }

      // Detect overlap with EXISTING (non-trip) calendar meetings — these
      // are meetings the rep set up before planning this trip, so the
      // trip planner asks before stomping over them. Locked 2026-02 #66.
      const stopsWithTime = (trip.stops || []).filter((s) => s.arrival_time && !s.meeting_id);
      if (stopsWithTime.length > 0) {
        try {
          // Use the trip's date range as the calendar window.
          const start = trip.start_date;
          const end = trip.end_date || trip.start_date;
          const { data: calData } = await api.get("/calendar/events", {
            params: { start, end },
          });
          const events = (calData || []).filter((e) => !e.trip_id || e.trip_id !== trip.id);
          const overlaps = [];
          for (const s of stopsWithTime) {
            const sStart = new Date(s.arrival_time).getTime();
            const sEnd = sStart + (Number(s.duration_min) || 60) * 60_000;
            for (const ev of events) {
              const eStart = new Date(ev.start).getTime();
              const eEnd = ev.end ? new Date(ev.end).getTime() : eStart + 60 * 60_000;
              if (sStart < eEnd && sEnd > eStart) {
                overlaps.push({ stop: s, event: ev });
              }
            }
          }
          if (overlaps.length > 0) {
            const lines = overlaps.map(({ stop, event }) =>
              `• Stop "${stop.title || "(no title)"}" at ${new Date(stop.arrival_time).toLocaleString()} overlaps "${event.title || event.name || "Existing meeting"}" at ${new Date(event.start).toLocaleString()}`
            ).join("\n");
            const ok = window.confirm(
              `⚠ Calendar overlap detected:\n\n${lines}\n\n` +
              `Activating this trip would create new meetings on top of these.\n\n` +
              `OK = save anyway (resolve manually after).\nCancel = pause to fix the times.`
            );
            if (!ok) { setSaving(false); return; }
          }
        } catch (_e) {
          // calendar fetch failed — don't block the save, just log
          // eslint-disable-next-line no-console
          console.warn("[Trip] overlap pre-check failed:", _e);
        }
      }

      const payload = {
        title: trip.title,
        start_date: trip.start_date,
        start_time: trip.start_time || null,
        end_date: trip.end_date,
        start_address: trip.start_address,
        start_lat: trip.start_lat,
        start_lng: trip.start_lng,
        start_place_id: trip.start_place_id,
        start_address_source: trip.start_address_source,
        notes: trip.notes,
        checklist: trip.checklist || [],
        stops: trip.stops || [],
      };
      const { data } = await api.put(`/trips/${id}`, payload);
      setTrip(data);
      originalRef.current = JSON.parse(JSON.stringify(data));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const [overlapPrompt, setOverlapPrompt] = useState(null); // { stops_with_overlap, decisions }

  const activate = async () => {
    const missingTimes = (trip.stops || []).filter((s) => !s.arrival_time).length;
    const total = (trip.stops || []).length;
    if (missingTimes > 0) {
      const warning = `${missingTimes} of ${total} stop${total === 1 ? "" : "s"} have no arrival time and will be SKIPPED (no calendar/task created). Activate anyway?`;
      if (!window.confirm(warning)) return;
    }
    setActivating(true);
    try {
      await save();
      // Pre-flight: ask the backend for any overlapping meetings so we can
      // show the per-stop "map / move / new" dialog instead of blindly
      // duplicating events on the user's calendar.
      const { data: overlap } = await api.get(`/trips/${id}/overlapping-meetings`);
      const conflicts = overlap?.stops_with_overlap || [];
      if (conflicts.length > 0) {
        setActivating(false);
        setOverlapPrompt({ stops: conflicts, decisions: {} });
        return; // user picks; activateWithDecisions runs next
      }
      const { data } = await api.post(`/trips/${id}/activate`, {});
      setTrip(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not activate");
    }
    setActivating(false);
  };

  const activateWithDecisions = async (decisions) => {
    setActivating(true);
    setOverlapPrompt(null);
    try {
      const { data } = await api.post(`/trips/${id}/activate`, { meeting_decisions: decisions });
      setTrip(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not activate");
    }
    setActivating(false);
  };

  const complete = async () => {
    if (!window.confirm("Mark this trip as completed? Any pending stops will be auto-marked as skipped.")) return;
    try {
      const { data } = await api.post(`/trips/${id}/complete`);
      setTrip(data);
      // Open debrief automatically — user just finished a day in the field
      setDebriefOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not complete");
    }
  };

  // Auto-open debrief when arriving via ?debrief=1 from a notification.
  useEffect(() => {
    if (searchParams.get("debrief") === "1" && trip?.status === "completed") {
      setDebriefOpen(true);
      // Strip the query param from the URL so reloads don't re-open the modal.
      const next = new URLSearchParams(searchParams);
      next.delete("debrief");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [trip?.status]);

  const setStop = (sid, patch) => {
    setTrip((t) => ({
      ...t,
      stops: (t.stops || []).map((s) => (s.id === sid ? { ...s, ...patch } : s)),
    }));
  };

  const removeStop = (sid) => setTrip((t) => ({ ...t, stops: (t.stops || []).filter((s) => s.id !== sid) }));

  const addStops = (items) => {
    // items: [{ lead_id?, customer_id?, title, address }]
    const existing = new Set((trip.stops || []).map((s) => `${s.lead_id || ""}:${s.customer_id || ""}`));
    const newStops = items
      .filter((it) => !existing.has(`${it.lead_id || ""}:${it.customer_id || ""}`))
      .map((it) => ({
        id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2),
        lead_id: it.lead_id || null,
        customer_id: it.customer_id || null,
        title: it.title,
        address: it.address || "",
        lat: it.lat || null,
        lng: it.lng || null,
        arrival_time: "",
        duration_min: 60,
        sales_brief: "",
        discussion_points: "",
        status: "pending",
      }));
    setTrip((t) => ({ ...t, stops: [...(t.stops || []), ...newStops] }));
    setPickerOpen(false);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const stops = trip.stops || [];
    const oldIdx = stops.findIndex((s) => s.id === active.id);
    const newIdx = stops.findIndex((s) => s.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    setTrip((t) => ({ ...t, stops: arrayMove(stops, oldIdx, newIdx) }));
  };

  const generateBrief = async (sid) => {
    setGenBriefFor(sid);
    try {
      // Save first so backend has latest stop info
      await save();
      const { data } = await api.post(`/trips/${id}/stops/${sid}/generate-brief`);
      setStop(sid, { sales_brief: data.brief, brief_trust_tier: data.trust_tier });
    } catch (_e) { /* noop */ }
    setGenBriefFor(null);
  };

  const markStop = async (sid, status) => {
    await api.patch(`/trips/${id}/stops/${sid}`, { status });
    load();
  };

  // Wave 25.17 — replaced the Google Maps Embed iframe with a Leaflet
  // route map. We pass the structured points array (with coords) to
  // TripRouteMap which uses OSM tiles + the public OSRM router (free).
  const routePoints = useMemo(() => {
    if (!trip) return [];
    const arr = [];
    if (trip.start_address || trip.start_place_id || trip.start_lat != null) {
      arr.push({
        label: trip.start_address, lat: trip.start_lat, lng: trip.start_lng,
        place_id: trip.start_place_id, kind: "depart", title: "Depart",
      });
    }
    (trip.stops || []).forEach((s, i) => {
      if (s.address || s.place_id || s.lat != null) {
        arr.push({
          label: s.address, lat: s.lat, lng: s.lng, place_id: s.place_id,
          kind: "stop", title: s.title || `Stop ${i + 1}`, idx: i + 1,
        });
      }
    });
    return arr;
  }, [trip]);
  const hasRoute = routePoints.filter((p) => p.lat != null && p.lng != null).length >= 2;

  const googleMapsNavUrl = useMemo(() => {
    if (!trip) return null;
    const points = [];
    if (trip.start_address) points.push(trip.start_address);
    (trip.stops || []).forEach((s) => { if (s.address) points.push(s.address); });
    if (points.length < 2) return null;
    const origin = encodeURIComponent(points[0]);
    const destination = encodeURIComponent(points[points.length - 1]);
    const waypoints = points.slice(1, -1).map(encodeURIComponent).join("|");
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ""}&travelmode=driving`;
  }, [trip]);

  if (loading) return <div className="text-xs text-[#647087] p-8 text-center">Loading trip…</div>;
  if (!trip) return (
    <div className="text-center p-8">
      <p className="text-sm text-[#a0aabf]">Trip not found.</p>
      <Link to="/trips" className="hv-btn hv-btn-ghost mt-4">Back to trips</Link>
    </div>
  );

  // Phase 7 Sub-iter 7D — EntityShell migration.
  const tripStatusTone = {
    draft: "slate",
    active: "amber",
    completed: "green",
    cancelled: "slate",
  }[trip.status] || "slate";
  const tripTabs = [
    { key: "plan",     label: "Plan" },
    { key: "map",      label: "Map" },
    { key: "calendar", label: "Calendar" },
    { key: "insights", label: "AI Insights" },
    { key: "itinerary", label: "Itinerary" },
  ];
  const headerActions = (
    <>
      {!isLocked && (
        <>
          <button type="button" onClick={save} disabled={saving} className="hv-btn hv-btn-ghost text-xs" data-testid="trip-save">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={activate}
            disabled={activating || (trip.stops || []).length === 0}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="trip-activate"
          >
            <Play className="w-3.5 h-3.5" /> {activating ? "Activating…" : "Activate trip"}
          </button>
        </>
      )}
      {trip.status === "active" && (
        <>
          <button
            type="button"
            onClick={async () => { await save(); await activate(); }}
            disabled={saving || activating}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="trip-save-reschedule"
            title="Save your time changes and re-sync the linked calendar meetings (you'll be asked to confirm before any meeting is moved)"
          >
            <Save className="w-3.5 h-3.5" /> {(saving || activating) ? "Saving…" : "Save & reschedule"}
          </button>
          <button
            type="button"
            onClick={complete}
            className="hv-btn hv-btn-ghost text-emerald-300 text-xs"
            data-testid="trip-complete"
            title="Mark trip as completed and close it out"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Complete
          </button>
        </>
      )}
      {trip.status === "completed" && (
        <button
          type="button"
          onClick={() => setDebriefOpen(true)}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="trip-debrief-open"
          title={`Talk to ${aiName} about each stop — automatically saves meeting notes to leads`}
        >
          <MessageSquareText className="w-3.5 h-3.5" /> Debrief day
        </button>
      )}
      {googleMapsNavUrl && (
        <a
          href={googleMapsNavUrl}
          target="_blank"
          rel="noreferrer"
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="trip-open-in-gmaps"
          title="Open route in Google Maps app for turn-by-turn navigation"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Navigate
        </a>
      )}
    </>
  );

  return (
    <EntityShell
      kindLabel="Trip"
      title={trip.title || "Trip"}
      status={{ label: trip.status || "draft", tone: tripStatusTone }}
      backTo="/trips"
      actions={headerActions}
      tabs={tripTabs}
      activeTab={tab}
      onTabChange={setTab}
    >
    <div className="space-y-4" data-testid="trip-detail-page">
      {/* Editable trip title (Plan tab only — Shell shows the static label) */}
      {tab === "plan" && (
      <div className="hv-card p-3 flex items-center gap-3 flex-wrap" data-testid="trip-title-editor">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Title</span>
        <input
          value={trip.title || ""}
          onChange={(e) => setTrip((t) => ({ ...t, title: e.target.value }))}
          disabled={isLocked}
          className="font-display text-lg md:text-xl font-bold tracking-tight bg-transparent border-0 border-b border-transparent hover:border-white/20 focus:border-[#1fb6c1] focus:outline-none flex-1 min-w-0"
          data-testid="trip-title"
        />
        {isLocked && <span className="flex items-center gap-1 text-[#F59E0B] text-xs"><Lock className="w-3 h-3" /> Locked — activated</span>}
        <DeepModePill
          trip={trip}
          onToggled={(enabled) => setTrip((t) => ({ ...t, deep_mode: enabled }))}
        />
      </div>
      )}

      {err && <div className="hv-card p-3 text-xs text-rose-300 border-rose-500/40">{err}</div>}


      {/* ─── PLAN TAB ─────────────────────────────────────────────── */}
      {tab === "plan" && (<>
      {/* Trip meta */}
      <div className="hv-card p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Start date">
          <HVDateInput
            value={trip.start_date || ""}
            onChange={(v) => setTrip((t) => ({ ...t, start_date: v }))}
            disabled={isLocked}
            mode="date"
            testId="trip-start-date"
          />
        </Field>
        <Field label="Departure time">
          <HVDateInput
            value={trip.start_time || ""}
            onChange={(v) => setTrip((t) => ({ ...t, start_time: v }))}
            disabled={isLocked}
            mode="time"
            testId="trip-start-time"
            placeholder="HH:MM"
          />
        </Field>
        <Field label="End date (optional)">
          <HVDateInput
            value={trip.end_date || ""}
            onChange={(v) => setTrip((t) => ({ ...t, end_date: v }))}
            disabled={isLocked}
            mode="date"
            testId="trip-end-date"
          />
        </Field>
        <Field label="Starting point">
          {isLocked ? (
            <input value={trip.start_address || ""} disabled className="hv-input text-sm" />
          ) : (
            <PlaceInput
              value={trip.start_address ? {
                address: trip.start_address,
                lat: trip.start_lat,
                lng: trip.start_lng,
                place_id: trip.start_place_id,
                source: trip.start_address_source || "legacy",
              } : null}
              onChange={(v) => setTrip((t) => ({
                ...t,
                start_address: v?.address || "",
                start_lat: v?.lat ?? null,
                start_lng: v?.lng ?? null,
                start_place_id: v?.place_id || null,
                start_address_source: v?.source || null,
              }))}
              placeholder="Search a town, address, or business…"
              testId="trip-start-address"
            />
          )}
        </Field>
        <Field label="Trip notes" className="sm:col-span-2 lg:col-span-4">
          <VoiceTextarea
            value={trip.notes || ""}
            onChange={(v) => setTrip((t) => ({ ...t, notes: v }))}
            disabled={isLocked}
            rows={3}
            placeholder="Overall trip goal, key dates, etc. Or tap the mic to dictate."
            className="text-sm"
            context="lead_activity"
            testIdPrefix="trip-notes-voice"
          />
        </Field>
      </div>

      {/* Trip Summary at-a-glance card */}
      <TripSummaryCard trip={trip} driveTimes={driveTimes} />

      {/* Departure-time required nag */}
      {!trip.start_time && (trip.stops || []).length > 0 && (
        <div className="hv-card p-3 border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs flex items-start gap-2" data-testid="trip-needs-departure-time">
          <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-amber-100">Departure time missing.</strong> Without a departure time, arrival times for stops can't be auto-calculated. Set the time above and we'll cascade the rest.
          </div>
        </div>
      )}

      {/* Map preview */}
      {hasRoute && (
        <div className="hv-card overflow-hidden" data-testid="trip-map">
          <TripRouteMap points={routePoints} height="18rem" className="border-0 rounded-none" />
          <div className="px-3 py-2 border-t border-white/5 flex items-center gap-2 flex-wrap text-xs">
            <button
              type="button"
              onClick={fetchDriveTimes}
              disabled={loadingDriveTimes}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="trip-fetch-drive-times"
              title="Fetch leg-by-leg distance + driving time"
            >
              <Clock className="w-3 h-3 text-[#1fb6c1]" />
              {loadingDriveTimes ? "Calculating…" : driveTimes ? "Refresh drive times" : "Show drive times"}
            </button>
            {driveTimes?.totals && (
              <span className="text-[#a0aabf] font-mono" data-testid="trip-drive-totals">
                Total: <span className="text-white">{driveTimes.totals.distance_text}</span> · <span className="text-[#1fb6c1]">{driveTimes.totals.duration_text}</span> driving
              </span>
            )}
            {driveTimes?.warning && (
              <span className="text-amber-300">{driveTimes.warning}</span>
            )}
          </div>
        </div>
      )}

      {/* Trip-level checklist (things to bring / reminders) */}
      <ChecklistCard
        items={trip.checklist || []}
        disabled={isLocked}
        onAdd={addChecklistItem}
        onToggle={toggleChecklistItem}
        onRemove={removeChecklistItem}
      />

      {/* Stops */}
      <div className="hv-card p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#1fb6c1]" />
            Stops <span className="text-xs font-mono text-[#647087]">({(trip.stops || []).length})</span>
          </h2>
          {!isLocked && (
            <button type="button" onClick={() => setPickerOpen(true)} className="hv-btn hv-btn-ghost text-xs" data-testid="trip-add-stops">
              <Plus className="w-3 h-3" /> Add stops
            </button>
          )}
          {!isLocked && (trip.stops || []).length > 0 && (
            <button
              type="button"
              onClick={resolveStops}
              disabled={resolvingStops}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="trip-resolve-stops"
              title="Match stops to existing leads, customers, prospects, sales briefs and meetings — auto-links high-confidence matches."
            >
              <LinkIcon className="w-3 h-3" />
              {resolvingStops ? "Resolving…" : "Auto-link stops"}
            </button>
          )}
          {(trip.stops || []).length > 1 && (
            <>
              <button type="button" onClick={collapseAll} className="text-[10px] text-[#647087] hover:text-[#1fb6c1]" data-testid="trip-collapse-all">Collapse all</button>
              <button type="button" onClick={expandAll} className="text-[10px] text-[#647087] hover:text-[#1fb6c1]" data-testid="trip-expand-all">Expand all</button>
            </>
          )}
        </div>
        {linkResolveMsg && (
          <div className="mb-3 px-3 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 text-[11px] text-emerald-200 flex items-center gap-2" data-testid="trip-resolve-msg">
            <LinkIcon className="w-3 h-3" /> {linkResolveMsg}
          </div>
        )}
        {!isLocked && (trip.stops || []).some((s) => !s.arrival_time) && (
          <div className="mb-3 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-200 flex items-center gap-2" data-testid="trip-missing-time-warning">
            <Clock className="w-3 h-3" />
            Some stops have no arrival time — they will be skipped during activation. Set a time to turn them into calendar events.
          </div>
        )}
        {(trip.stops || []).length === 0 ? (
          <div className="text-center py-8 text-xs text-[#647087]">
            No stops yet. Click <strong>Add stops</strong> to pick leads/customers to visit.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={(trip.stops || []).map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {(trip.stops || []).map((stop, idx) => {
                  // Leg index: leg[0] = start_address → stops[0]; leg[i] = stops[i-1] → stops[i]
                  // If start_address present, idx maps to leg[idx]; else idx maps to leg[idx-1] (skip first stop label)
                  const legIdx = trip.start_address ? idx : idx - 1;
                  const leg = legIdx >= 0 ? driveTimes?.legs?.[legIdx] : null;
                  return (
                    <div key={stop.id}>
                      {leg && leg.ok && (
                        <div
                          className={`ml-4 my-1 text-[10px] font-mono flex items-center gap-1.5 pl-2 border-l border-dashed ${leg.manual ? "text-amber-200 border-amber-400/40" : "text-[#647087] border-white/15"}`}
                          data-testid={`trip-leg-${legIdx}`}
                        >
                          <Clock className={`w-2.5 h-2.5 ${leg.manual ? "text-amber-300" : "text-[#1fb6c1]"}`} />
                          <span className={leg.manual ? "text-amber-100" : "text-[#a0aabf]"}>{leg.duration_text}</span>
                          {leg.distance_text && leg.distance_text !== "—" && (
                            <>
                              <span>·</span>
                              <span>{leg.distance_text}</span>
                            </>
                          )}
                          <span className={leg.manual ? "text-amber-300/70" : "text-[#647087]"}>{leg.manual ? "manual" : "drive"}</span>
                        </div>
                      )}
                      {leg && !leg.ok && (
                        <div
                          className="ml-4 my-1 text-[10px] flex items-center gap-1.5 pl-2 border-l border-dashed border-rose-400/40 text-rose-200"
                          data-testid={`trip-leg-${legIdx}`}
                        >
                          <Clock className="w-2.5 h-2.5 text-rose-300" />
                          No route — set a manual drive time on this stop.
                        </div>
                      )}
                      <SortableStopCard
                        stop={stop}
                        idx={idx}
                        isLocked={isLocked}
                        arrivalEditable={arrivalEditable}
                        isCollapsed={collapsed.has(stop.id)}
                        onToggleCollapsed={() => toggleCollapsed(stop.id)}
                        legSummary={leg?.ok ? leg.duration_text : null}
                        onChange={(p) => setStop(stop.id, p)}
                        onRemove={() => removeStop(stop.id)}
                        onGenerateBrief={() => generateBrief(stop.id)}
                        generatingBrief={genBriefFor === stop.id}
                        onMarkVisited={() => markStop(stop.id, "visited")}
                        onMarkSkipped={() => markStop(stop.id, "skipped")}
                        suggestion={linkSuggestions[stop.id]}
                        onLinkPick={(body) => linkStop(stop.id, body)}
                      />
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Stop picker */}
      {pickerOpen && (
        <StopPicker onClose={() => setPickerOpen(false)} onPick={addStops} existingStops={trip.stops || []} tripStartLatLng={trip.start_lat != null && trip.start_lng != null ? [trip.start_lat, trip.start_lng] : null} />
      )}
      </>)}

      {/* ─── MAP TAB ─────────────────────────────────────────────── */}
      {tab === "map" && <TripMapTab trip={trip} googleMapsNavUrl={googleMapsNavUrl} />}

      {/* ─── CALENDAR TAB ────────────────────────────────────────── */}
      {tab === "calendar" && <TripCalendarTab trip={trip} />}

      {/* ─── AI INSIGHTS TAB ─────────────────────────────────────── */}
      {tab === "insights" && <TripInsightsTab trip={trip} driveTimes={driveTimes} />}

      {/* ─── ITINERARY (PRINT) TAB ───────────────────────────────── */}
      {tab === "itinerary" && <TripItineraryTab trip={trip} driveTimes={driveTimes} googleMapsNavUrl={googleMapsNavUrl} />}

      {overlapPrompt && (
        <OverlapResolverDialog
          stopsWithOverlap={overlapPrompt.stops}
          onCancel={() => setOverlapPrompt(null)}
          onConfirm={activateWithDecisions}
        />
      )}

      {debriefOpen && trip && (
        <DebriefModal
          trip={trip}
          onClose={() => setDebriefOpen(false)}
          onCommitted={async () => {
            // Refresh trip so the debrief_status flag updates
            try {
              const fresh = await api.get(`/trips/${id}`);
              setTrip(fresh.data);
            } catch (_e) { /* noop */ }
          }}
        />
      )}
    </div>
    </EntityShell>
  );
}

/* ─── Overlap Resolver Dialog (Wave 13 — 2026-02 #71) ────────────────
   Shown right before activate when the backend reports the user has
   existing meetings overlapping any trip stop. The user picks per stop:
     • Map (override travel time, meeting time wins → trip cascade re-aligns)
     • Map (move meeting to stop time + update meeting address)
     • Create new (default, current behaviour)
   Decisions are sent to /trips/:id/activate which then re-uses or
   inserts the right meeting per choice. */
function OverlapResolverDialog({ stopsWithOverlap, onCancel, onConfirm }) {
  const [decisions, setDecisions] = useState(() => {
    // Default: create new for everything (preserves old behaviour if
    // user just hits Confirm without changing anything).
    const d = {};
    stopsWithOverlap.forEach((s) => { d[s.stop_id] = { action: "create", meeting_id: "" }; });
    return d;
  });
  const update = (stopId, patch) => setDecisions((d) => ({ ...d, [stopId]: { ...d[stopId], ...patch } }));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3" data-testid="trip-overlap-dialog">
      <div className="hv-card max-w-2xl w-full p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-300" />
          <h3 className="font-display text-lg font-bold">Existing meetings overlap</h3>
        </div>
        <p className="text-xs text-[#a0aabf] mb-4">
          Some of your trip stops overlap meetings already on your calendar. Pick how to handle each — mapping a stop to an existing meeting prevents duplicate calendar entries.
        </p>
        <div className="space-y-3">
          {stopsWithOverlap.map((s) => (
            <div key={s.stop_id} className="border border-white/10 rounded-md p-3" data-testid={`trip-overlap-stop-${s.stop_id}`}>
              <div className="text-sm font-bold mb-1">{s.stop_title || "Stop"}</div>
              <div className="text-[10px] font-mono text-[#647087] mb-2">
                {new Date(s.stop_start).toLocaleString()} → {new Date(s.stop_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>

              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Conflicting meeting</label>
              <select
                value={decisions[s.stop_id]?.meeting_id || ""}
                onChange={(e) => update(s.stop_id, { meeting_id: e.target.value, action: e.target.value ? (decisions[s.stop_id]?.action === "create" ? "map_use_stop_time" : decisions[s.stop_id]?.action) : "create" })}
                className="hv-input w-full text-xs mt-1"
                data-testid={`trip-overlap-meeting-${s.stop_id}`}
              >
                <option value="">— don't map (create new meeting) —</option>
                {(s.overlaps || []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title} · {new Date(m.start).toLocaleString()}{m.location ? ` · ${m.location}` : ""}
                  </option>
                ))}
              </select>

              {decisions[s.stop_id]?.meeting_id && (
                <div className="mt-2 space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">When mapped, what should win?</label>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="radio"
                        name={`action-${s.stop_id}`}
                        checked={decisions[s.stop_id]?.action === "map_use_stop_time"}
                        onChange={() => update(s.stop_id, { action: "map_use_stop_time" })}
                        data-testid={`trip-overlap-action-stop-${s.stop_id}`}
                      />
                      <span><strong>Stop time wins</strong> — move the meeting to the stop's arrival time + update its address</span>
                    </label>
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="radio"
                        name={`action-${s.stop_id}`}
                        checked={decisions[s.stop_id]?.action === "map_keep_meeting_time"}
                        onChange={() => update(s.stop_id, { action: "map_keep_meeting_time" })}
                        data-testid={`trip-overlap-action-meeting-${s.stop_id}`}
                      />
                      <span><strong>Meeting time wins</strong> — adjust the stop's arrival time to the meeting (trip schedule re-cascades)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-white/10">
          <button type="button" onClick={onCancel} className="hv-btn hv-btn-ghost text-xs" data-testid="trip-overlap-cancel">Cancel</button>
          <button type="button" onClick={() => onConfirm(decisions)} className="hv-btn hv-btn-primary text-xs" data-testid="trip-overlap-confirm">
            Activate with these decisions
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ─── Trip Summary at-a-glance card ──────────────────────────────────
   Shown at the top of the trip editor. Renders a creative timeline:
   start point → leg drive → stop → leg drive → stop … with arrival
   times computed from `start_time + cumulative travel + duration`.
   Cascades automatically when the user edits any time/duration. */
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
function _humanMin(m) {
  if (m == null || !Number.isFinite(Number(m))) return "—";
  const mn = Math.round(Number(m));
  if (mn < 60) return `${mn}m`;
  const h = Math.floor(mn / 60); const r = mn % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}
function TripSummaryCard({ trip, driveTimes }) {
  const stops = trip?.stops || [];
  const hasDepart = !!(trip?.start_date && trip?.start_time);
  // Backend returns `duration_s`; derive minutes for display + math.
  const totalDriveMin = (driveTimes?.totals?.duration_min)
    ?? (driveTimes?.totals?.duration_s != null ? Math.round(driveTimes.totals.duration_s / 60) : null);
  const totalStopMin = stops.reduce((acc, s) => acc + (Number(s.duration_min) || 60), 0);
  const totalAllMin = (totalDriveMin || 0) + totalStopMin;
  let finishIso = null;
  if (hasDepart) {
    try {
      const last = stops[stops.length - 1];
      if (last?.arrival_time) {
        finishIso = new Date(new Date(last.arrival_time).getTime() + (Number(last.duration_min) || 60) * 60_000).toISOString();
      }
    } catch { /* noop */ }
  }
  return (
    <div className="hv-card p-4 border-[#1fb6c1]/30 bg-gradient-to-br from-[#0c1729] via-[#0c1320] to-[#091020]" data-testid="trip-summary-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#1fb6c1] animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#1fb6c1] font-semibold">Trip overview</span>
        </div>
        <div className="text-[11px] text-[#a0aabf] flex items-center gap-3 font-mono flex-wrap">
          <span><strong className="text-white">{stops.length}</strong> stop{stops.length === 1 ? "" : "s"}</span>
          <span>•</span>
          <span>Drive <strong className="text-[#1fb6c1]">{driveTimes?.totals?.duration_text || _humanMin(totalDriveMin) || "—"}</strong></span>
          <span>•</span>
          <span>Stops <strong className="text-amber-300">{_humanMin(totalStopMin)}</strong></span>
          {finishIso && (<><span>•</span><span>Done by <strong className="text-white">{fmtTime(finishIso)}</strong></span></>)}
        </div>
      </div>
      {!hasDepart || stops.length === 0 ? (
        <div className="text-xs text-[#a0aabf] italic">
          {stops.length === 0
            ? "Add stops below to build the route."
            : "Set the departure time above to see the at-a-glance schedule."}
        </div>
      ) : (
        <ol className="space-y-2" data-testid="trip-summary-timeline">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1fb6c1]/20 border border-[#1fb6c1]/50 text-[10px] flex items-center justify-center text-[#1fb6c1] font-mono font-bold">▶</span>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[#a0aabf]">Depart</div>
              <div className="text-sm text-white truncate">{trip.start_address || "Not set"}</div>
              <div className="text-[11px] text-[#1fb6c1] font-mono">{trip.start_time || "—"} · {trip.start_date || "—"}</div>
            </div>
          </li>
          {stops.map((s, i) => {
            const leg = driveTimes?.legs?.[i];
            const placeholder = !s.address && s.lat == null;
            return (
              <li key={s.id} className="flex items-start gap-3">
                <span className={`flex-shrink-0 w-6 h-6 rounded-full text-[10px] flex items-center justify-center font-mono font-bold border ${placeholder ? "bg-amber-500/15 border-amber-500/50 text-amber-300" : "bg-white/5 border-white/15 text-white"}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white truncate">{s.title || `Stop ${i + 1}`}</span>
                    {leg && leg.duration_text && (
                      <span className="text-[10px] text-[#a0aabf] font-mono">+{leg.duration_text} drive · {leg.distance_text}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#a0aabf] truncate">{s.address || (placeholder ? "⚠ Address missing — add one below" : "")}</div>
                  <div className="text-[11px] font-mono">
                    <span className="text-[#1fb6c1]">{fmtTime(s.arrival_time)}</span>
                    <span className="text-[#a0aabf]"> · {_humanMin(s.duration_min || 60)} on-site</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-[#a0aabf]">
        Total day: <span className="text-white font-mono">{_humanMin(totalAllMin)}</span> (drive + on-site). Edit any time to recalculate the rest.
      </div>
    </div>
  );
}

/* ─── Cascade arrival times from departure across stops ─────────────
   Pure function. Given start_date + start_time + drive_legs (Google
   Directions response), compute each stop's `arrival_time`. Uses
   `manual_travel_min` if set, otherwise the leg's duration. Stops
   with `manual_arrival_time` set keep their override.  Stops where
   no travel info is available get a NULL arrival_time so the user
   is forced to enter an estimate. */
function _cascadeArrivalTimes({ start_date, start_time, stops, drive_legs }) {
  if (!start_date || !start_time || !stops || stops.length === 0) return stops;
  let cursor;
  try {
    cursor = new Date(`${start_date}T${start_time}:00`);
    if (!Number.isFinite(cursor.getTime())) return stops;
  } catch { return stops; }
  return stops.map((s, i) => {
    const leg = drive_legs?.[i];
    // Backend returns `duration_s` (seconds) on each leg; convert here.
    // We also accept a `duration_min` field for forward-compat.
    const legMin = leg?.duration_min != null
      ? Number(leg.duration_min)
      : (leg?.duration_s != null ? Number(leg.duration_s) / 60 : null);
    const travelMin = (s.manual_travel_min != null && s.manual_travel_min !== "")
      ? Number(s.manual_travel_min)
      : legMin;
    if (travelMin != null && Number.isFinite(travelMin) && travelMin >= 0) {
      cursor = new Date(cursor.getTime() + travelMin * 60_000);
    } else {
      // No travel info — break the cascade for this and every later stop.
      // The summary card flags these with "—" so the user knows to enter
      // a manual_travel_min.
      const dur = Number(s.duration_min) || 60;
      const next = { ...s, arrival_time: s.manual_arrival_time || null };
      cursor = new Date(NaN);
      void dur;
      return next;
    }
    const arrivalIso = (s.manual_arrival_time && s.manual_arrival_time.trim())
      ? s.manual_arrival_time
      : cursor.toISOString();
    const dur = Number(s.duration_min) || 60;
    cursor = new Date(cursor.getTime() + dur * 60_000);
    return { ...s, arrival_time: arrivalIso };
  });
}

function SortableStopCard({ stop, idx, isLocked, arrivalEditable, isCollapsed, onToggleCollapsed, legSummary, onChange, onRemove, onGenerateBrief, generatingBrief, onMarkVisited, onMarkSkipped, suggestion, onLinkPick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const statusColor = stop.status === "visited" ? "#10B981" : stop.status === "skipped" ? "#ef4444" : "#1fb6c1";
  const linkTo = stop.lead_id ? `/leads/${stop.lead_id}` : stop.customer_id ? `/customers/${stop.customer_id}` : null;
  const fmtArrival = stop.arrival_time
    ? new Date(stop.arrival_time).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })
    : null;
  // Pre-compute the "pretty link target" for each entity kind for the chip
  // row. The suggestion structure is:
  //   { auto_applied?, lead_matches:[{id,name,score}], customer_matches:[…],
  //     suspect_matches:[…], brief?:{…}, existing_meeting?:{…} }
  const sug = suggestion || null;
  const isLinked = !!(stop.lead_id || stop.customer_id || stop.suspect_id);
  return (
    <div ref={setNodeRef} style={style} className="border border-white/10 rounded-md overflow-hidden" data-testid={`trip-stop-${stop.id}`}>
      <div className="flex items-start gap-2 p-3 bg-white/[0.02]">
        {!isLocked && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-[#647087] hover:text-white p-0.5 mt-0.5"
            title="Drag to reorder"
            data-testid={`trip-stop-handle-${stop.id}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="text-[#647087] hover:text-[#1fb6c1] mt-0.5 p-0.5"
          title={isCollapsed ? "Expand stop" : "Collapse stop"}
          data-testid={`trip-stop-toggle-${stop.id}`}
        >
          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
          style={{ background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}60` }}
        >
          {idx + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-sm font-bold truncate">
              {linkTo ? (
                <Link to={linkTo} className="hover:text-[#1fb6c1]" data-testid={`trip-stop-link-${stop.id}`}>
                  {stop.title}
                </Link>
              ) : stop.title}
            </div>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ borderColor: `${statusColor}55`, color: statusColor }}>
              {stop.status}
            </span>
            {stop.meeting_id && <span className="text-[10px] text-[#A78BFA] flex items-center gap-1"><Calendar className="w-3 h-3" /> calendar linked</span>}
            {/* Collapsed-mode summary chips */}
            {isCollapsed && fmtArrival && <span className="text-[10px] text-[#1fb6c1] flex items-center gap-1"><Clock className="w-3 h-3" />{fmtArrival}</span>}
            {isCollapsed && stop.duration_min && <span className="text-[10px] text-[#647087]">· {stop.duration_min}min</span>}
            {isCollapsed && legSummary && <span className="text-[10px] text-[#a0aabf]">· {legSummary} drive</span>}
          </div>
          {stop.address && <div className="text-[10px] font-mono text-[#647087] mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{stop.address}</div>}
          {/* Linked-entity chips: customer / lead / suspect / brief / meeting.
              These render whenever the stop has a direct FK or the resolver
              found a high-confidence match it could surface. The user can
              jump to any linked entity in one click. */}
          {(stop.customer_id || stop.lead_id || stop.suspect_id || sug?.brief || sug?.existing_meeting) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`trip-stop-links-${stop.id}`}>
              {stop.customer_id && (
                <Link to={`/customers/${stop.customer_id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 bg-[#1fb6c1]/10 text-[#1fb6c1] hover:bg-[#1fb6c1]/20 inline-flex items-center gap-1" data-testid={`trip-stop-chip-customer-${stop.id}`}>
                  <Building2 className="w-2.5 h-2.5" /> Customer
                </Link>
              )}
              {stop.lead_id && (
                <Link to={`/leads/${stop.lead_id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 inline-flex items-center gap-1" data-testid={`trip-stop-chip-lead-${stop.id}`}>
                  <Sparkles className="w-2.5 h-2.5" /> Lead
                </Link>
              )}
              {stop.suspect_id && (
                <Link to={`/suspects/${stop.suspect_id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 inline-flex items-center gap-1" data-testid={`trip-stop-chip-suspect-${stop.id}`}>
                  <Search className="w-2.5 h-2.5" /> Prospect
                </Link>
              )}
              {sug?.brief && (
                <Link to={`/research/${sug.brief.id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20 inline-flex items-center gap-1" data-testid={`trip-stop-chip-brief-${stop.id}`}>
                  <FileText className="w-2.5 h-2.5" /> Sales brief
                  {sug.brief.trust_tier && <span className="opacity-70">· {sug.brief.trust_tier}</span>}
                </Link>
              )}
              {sug?.existing_meeting && (
                <Link to={`/calendar?meeting=${sug.existing_meeting.id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 inline-flex items-center gap-1" data-testid={`trip-stop-chip-meeting-${stop.id}`}>
                  <Calendar className="w-2.5 h-2.5" /> Meeting
                </Link>
              )}
            </div>
          )}
          {/* Resolver suggestions for unlinked stops — render a compact
              "link this to…" picker per pool, capped at top-3 each. Hidden
              when stop is already linked or no suggestions are available. */}
          {!isLinked && !isLocked && sug && (
            (sug.lead_matches?.length || sug.customer_matches?.length || sug.suspect_matches?.length) ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]" data-testid={`trip-stop-suggest-${stop.id}`}>
                <span className="text-[#647087]">Link to:</span>
                {(sug.customer_matches || []).slice(0, 2).map((m) => (
                  <button
                    key={`c-${m.id}`}
                    type="button"
                    onClick={() => onLinkPick({ customer_id: m.id })}
                    className="px-1.5 py-0.5 rounded border border-[#1fb6c1]/30 text-[#1fb6c1] hover:bg-[#1fb6c1]/10 inline-flex items-center gap-1"
                    title={`Customer · score ${m.score}`}
                    data-testid={`trip-stop-pick-customer-${stop.id}-${m.id}`}
                  >
                    <Building2 className="w-2.5 h-2.5" /> {m.name}
                  </button>
                ))}
                {(sug.lead_matches || []).slice(0, 2).map((m) => (
                  <button
                    key={`l-${m.id}`}
                    type="button"
                    onClick={() => onLinkPick({ lead_id: m.id })}
                    className="px-1.5 py-0.5 rounded border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1"
                    title={`Lead · score ${m.score}`}
                    data-testid={`trip-stop-pick-lead-${stop.id}-${m.id}`}
                  >
                    <Sparkles className="w-2.5 h-2.5" /> {m.name}
                  </button>
                ))}
                {(sug.suspect_matches || []).slice(0, 2).map((m) => (
                  <button
                    key={`s-${m.id}`}
                    type="button"
                    onClick={() => onLinkPick({ suspect_id: m.id })}
                    className="px-1.5 py-0.5 rounded border border-amber-400/30 text-amber-300 hover:bg-amber-500/10 inline-flex items-center gap-1"
                    title={`Prospect · score ${m.score}`}
                    data-testid={`trip-stop-pick-suspect-${stop.id}-${m.id}`}
                  >
                    <Search className="w-2.5 h-2.5" /> {m.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-1.5 text-[10px] text-[#647087]" data-testid={`trip-stop-no-match-${stop.id}`}>
                No matching customer / lead / prospect found. Re-run "Auto-link" after creating one.
              </div>
            )
          )}
          {(stop.contact_name || stop.contact_phone) && (
            <div className="text-[10px] text-[#a0aabf] mt-0.5 flex items-center gap-2 flex-wrap">
              {stop.contact_name && <span><User className="w-2.5 h-2.5 inline mr-1" />{stop.contact_name}</span>}
              {stop.contact_phone && (
                <>
                  <a href={`tel:${stop.contact_phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#1fb6c1] flex items-center gap-1" data-testid={`trip-stop-call-${stop.id}`}>
                    <Phone className="w-2.5 h-2.5" /> {stop.contact_phone}
                  </a>
                  <a href={`https://wa.me/${String(stop.contact_phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-emerald-300 hover:text-emerald-200 flex items-center gap-1" data-testid={`trip-stop-wa-${stop.id}`}>
                    <MessageSquare className="w-2.5 h-2.5" /> WhatsApp
                  </a>
                </>
              )}
            </div>
          )}
        </div>
        {!isLocked && (
          <button type="button" onClick={onRemove} className="hv-btn hv-btn-ghost px-1.5 py-1 text-xs" data-testid={`trip-stop-remove-${stop.id}`}>
            <Trash2 className="w-3 h-3 text-rose-300" />
          </button>
        )}
        {isLocked && stop.status === "pending" && (
          <>
            <button type="button" onClick={onMarkVisited} className="hv-btn hv-btn-primary text-[10px] py-1 px-2" data-testid={`trip-stop-visited-${stop.id}`}>
              <Check className="w-3 h-3" /> Visited
            </button>
            <button type="button" onClick={onMarkSkipped} className="hv-btn hv-btn-ghost text-[10px] py-1 px-2" data-testid={`trip-stop-skip-${stop.id}`}>
              Skip
            </button>
          </>
        )}
      </div>
      {!isCollapsed && (
        <div className="p-3 grid sm:grid-cols-2 gap-2 border-t border-white/5">
        {/* Missing-address banner — picker couldn't find an address from
            the lead, so prompt the rep to set one (otherwise no drive
            time is computable and the cascade breaks). */}
        {!isLocked && !stop.address && stop.lat == null && (
          <div className="sm:col-span-2 p-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] flex items-start gap-2" data-testid={`trip-stop-missing-address-${stop.id}`}>
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-amber-100">No address on file for this lead.</strong> Set one below — search by name, drop a pin, or paste a Plus Code. Without it, drive time can't be auto-calculated and you'll need to enter a manual estimate.
            </div>
          </div>
        )}
        <Field label="Arrival time">
          <HVDateInput
            value={stop.arrival_time ? stop.arrival_time.slice(0, 16) : ""}
            onChange={(v) => onChange({
              manual_arrival_time: v ? new Date(v).toISOString() : "",
              arrival_time: v ? new Date(v).toISOString() : "",
            })}
            disabled={!arrivalEditable}
            mode="datetime"
            testId={`trip-stop-arrival-${stop.id}`}
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            value={stop.duration_min ?? 60}
            onChange={(e) => onChange({ duration_min: Number(e.target.value) || 60 })}
            disabled={!arrivalEditable}
            className="hv-input text-sm"
            data-testid={`trip-stop-duration-${stop.id}`}
          />
        </Field>
        <Field label="Address (for maps)" className="sm:col-span-2">
          {isLocked ? (
            <input value={stop.address || ""} disabled className="hv-input text-sm" />
          ) : (
            <PlaceInput
              value={stop.address ? {
                address: stop.address,
                lat: stop.lat,
                lng: stop.lng,
                place_id: stop.place_id,
                source: stop.address_source || (stop.lat && stop.lng ? "legacy" : "manual"),
              } : null}
              onChange={(v) => onChange({
                address: v?.address || "",
                lat: v?.lat ?? null,
                lng: v?.lng ?? null,
                place_id: v?.place_id || null,
                address_source: v?.source || null,
                // Reset manual_travel_min if the user just verified the address
                ...((v?.source === "places" || v?.source === "pin" || v?.source === "pluscode") ? { manual_travel_min: null } : {}),
              })}
              placeholder="e.g. Singita Lebombo, Kruger National Park"
              testId={`trip-stop-address-${stop.id}`}
            />
          )}
        </Field>
        {arrivalEditable && (
          <Field label={`Manual drive time to here (min)${(stop.address_source === "manual" || stop.address_source === "coords" || stop.address_source === "legacy" || !stop.lat) ? " — required" : " — optional override"}`}>
            <input
              type="number"
              value={stop.manual_travel_min ?? ""}
              onChange={(e) => onChange({ manual_travel_min: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="e.g. 90"
              className="hv-input text-sm"
              min={0} max={600}
              data-testid={`trip-stop-travel-min-${stop.id}`}
              title="Set this to override Google's estimate or to fill in when no address is verified. The summary above cascades from this value."
            />
          </Field>
        )}
        <Field label="Sales brief" className="sm:col-span-2">
          <div className="flex gap-2 items-start">
            <div className="flex-1 min-w-0">
              <VoiceTextarea
                value={stop.sales_brief || ""}
                onChange={(v) => onChange({ sales_brief: v })}
                disabled={isLocked}
                rows={4}
                placeholder="Key talking points, deal positioning, open questions… Or tap the mic."
                className="text-sm w-full"
                context="lead_activity"
                testIdPrefix={`trip-stop-brief-voice-${stop.id}`}
                data-testid={`trip-stop-brief-${stop.id}`}
              />
              {stop.brief_trust_tier && (
                <div className="mt-1 text-[10px] flex items-center gap-1" data-testid={`trip-stop-brief-trust-${stop.id}`}>
                  {stop.brief_trust_tier === "verified" && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">✓ Verified (online + internal)</span>
                  )}
                  {stop.brief_trust_tier === "internal_only" && (
                    <span className="px-1.5 py-0.5 rounded bg-[#1fb6c1]/10 text-[#1fb6c1] border border-[#1fb6c1]/30">internal facts only</span>
                  )}
                  {stop.brief_trust_tier === "refused" && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">⚠ insufficient data — add facts above</span>
                  )}
                  {stop.brief_trust_tier === "fallback" && (
                    <span className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/30">AI unavailable — showing raw facts</span>
                  )}
                </div>
              )}
              {stop.sales_brief && stop.brief_trust_tier && stop.brief_trust_tier !== "refused" && (
                <HidroFeedbackBar kind="brief" targetId={stop.id} compact />
              )}
            </div>
            {!isLocked && (
              <button
                type="button"
                onClick={onGenerateBrief}
                disabled={generatingBrief}
                className="hv-btn hv-btn-ghost text-xs shrink-0"
                title="Auto-generate a sales brief from the lead/customer context"
                data-testid={`trip-stop-gen-brief-${stop.id}`}
              >
                <Sparkles className="w-3 h-3 text-[#1fb6c1]" />
                {generatingBrief ? "…" : "AI"}
              </button>
            )}
            {stop.lead_id && (
              <Link
                to={`/research/lead/${stop.lead_id}`}
                onClick={(e) => e.stopPropagation()}
                className="hv-btn hv-btn-ghost text-xs shrink-0 text-[#A78BFA]"
                title="Open full deep-research report (Google Places + reviews + website + internal intel)"
                data-testid={`trip-stop-deep-${stop.id}`}
              >
                <ExternalLink className="w-3 h-3" /> Deep
              </Link>
            )}
          </div>
        </Field>
        <Field label="Discussion points / agenda" className="sm:col-span-2">
          <VoiceTextarea
            value={stop.discussion_points || ""}
            onChange={(v) => onChange({ discussion_points: v })}
            disabled={isLocked}
            rows={3}
            placeholder="Questions to ask, objections to handle, commercials to discuss. Or tap the mic."
            className="text-sm"
            context="lead_activity"
            testIdPrefix={`trip-stop-disc-voice-${stop.id}`}
          />
        </Field>
      </div>
      )}
    </div>
  );
}

function StopPicker({ onClose, onPick, existingStops, tripStartLatLng }) {
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [tab, setTab] = useState("leads");
  const [sort, setSort] = useState("heat"); // heat | phase | recent | az | distance
  const [filters, setFilters] = useState({
    industry: "", province: "", phaseHot: false, has5Star: false, lastContactCold: false,
  });

  useEffect(() => {
    (async () => {
      const [l, c] = await Promise.all([
        api.get("/leads").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
      ]);
      setLeads(l.data || []);
      setCustomers(c.data || []);
    })();
  }, []);

  const existingKeys = new Set(
    existingStops.map((s) => `${s.lead_id || ""}:${s.customer_id || ""}`),
  );

  // Better label resolver — prefer prospect's actual name, never the phase
  const resolveLeadName = (l) => {
    const candidate = (l.customer_name && l.customer_name.trim())
      || (l.title && !/^(lead onboarding|new prospect|fact[- ]find|callout|design|estimate|so |won|lost)/i.test(l.title.trim()) ? l.title.trim() : "")
      || (l.title && l.title.trim())
      || "(unnamed lead)";
    return candidate;
  };

  // Heat score: combines lead.heat_score (0-100) with recency penalty
  const computeRowHeat = (l) => {
    let h = Number(l.heat_score || 0);
    if (l.next_followup_date) {
      const days = (Date.now() - new Date(l.next_followup_date).getTime()) / 86400000;
      if (days > 60) h -= 20;  // way overdue
      else if (days > 14) h -= 5;
    }
    if (["estimate_sent", "so_sent", "design_quote"].includes(l.phase)) h += 15;
    if (Number(l.star_rating) >= 5) h += 5;
    return Math.max(0, Math.min(100, Math.round(h)));
  };

  const lastContactDays = (l) => {
    const t = l.next_followup_date || l.first_contact_date || l.updated_at || l.created_at;
    if (!t) return null;
    return Math.round((Date.now() - new Date(t).getTime()) / 86400000);
  };

  const PHASE_RANK = { estimate_sent: 1, so_sent: 2, design_quote: 3, callout_quote: 4, lead_logged: 5, lead_paused: 6, won: 7, lost: 8 };
  const PHASE_LABEL = {
    lead_logged: "Lead", callout_quote: "Callout", design_quote: "Design",
    estimate_sent: "Estimate", so_sent: "SO", won: "Won", lost: "Lost", lead_paused: "Paused",
  };

  // Distance helper (haversine, km) — used when the user toggles distance sort
  const haversine = (a, b) => {
    if (!a || !b || a[0] == null || b[0] == null) return Infinity;
    const R = 6371;
    const dLat = (b[0] - a[0]) * Math.PI / 180;
    const dLng = (b[1] - a[1]) * Math.PI / 180;
    const lat1 = a[0] * Math.PI / 180;
    const lat2 = b[0] * Math.PI / 180;
    const x = Math.sin(dLat/2)**2 + Math.sin(dLng/2)**2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(x));
  };

  // Build distinct industries / provinces for the filter dropdowns
  const allIndustries = useMemo(() => {
    return Array.from(new Set(leads.map((l) => l.industry).filter(Boolean))).sort();
  }, [leads]);
  const allProvinces = useMemo(() => {
    return Array.from(new Set(leads.map((l) => l.province_area).filter(Boolean))).sort();
  }, [leads]);

  let filteredLeads = leads.filter((l) => {
    if (existingKeys.has(`${l.id}:`)) return false;
    if (filters.industry && l.industry !== filters.industry) return false;
    if (filters.province && l.province_area !== filters.province) return false;
    if (filters.has5Star && Number(l.star_rating || 0) < 5) return false;
    if (filters.phaseHot && !["callout_quote", "design_quote", "estimate_sent", "so_sent"].includes(l.phase)) return false;
    if (filters.lastContactCold) {
      const d = lastContactDays(l);
      if (d == null || d < 60) return false;
    }
    const q = query.toLowerCase();
    if (!q) return true;
    return [resolveLeadName(l), l.title, l.customer_name, l.province_area, l.nearest_town, l.estate_reserve, l.industry]
      .some((v) => String(v || "").toLowerCase().includes(q));
  });
  // Apply sort
  filteredLeads = [...filteredLeads].sort((a, b) => {
    if (sort === "heat") return computeRowHeat(b) - computeRowHeat(a);
    if (sort === "phase") return (PHASE_RANK[a.phase] || 9) - (PHASE_RANK[b.phase] || 9);
    if (sort === "recent") {
      const da = lastContactDays(a) ?? 9999;
      const db = lastContactDays(b) ?? 9999;
      return da - db;
    }
    if (sort === "az") return resolveLeadName(a).localeCompare(resolveLeadName(b));
    if (sort === "distance" && tripStartLatLng) {
      const da = haversine(tripStartLatLng, [a.lat, a.lng]);
      const db = haversine(tripStartLatLng, [b.lat, b.lng]);
      return da - db;
    }
    return 0;
  });

  const filteredCustomers = customers.filter((c) => {
    if (existingKeys.has(`:${c.id}`)) return false;
    const q = query.toLowerCase();
    return !q || [c.name, c.industry, c.physical_address].some((v) => String(v || "").toLowerCase().includes(q));
  });

  // Hidro suggestions: top 5 hot, geographically nearest leads
  const hidroSuggested = useMemo(() => {
    if (!tripStartLatLng) return [];
    return [...leads]
      .filter((l) => !existingKeys.has(`${l.id}:`) && l.lat != null && l.lng != null)
      .map((l) => ({ ...l, _heat: computeRowHeat(l), _dist: haversine(tripStartLatLng, [l.lat, l.lng]) }))
      .filter((l) => l._dist < 200)
      .sort((a, b) => (b._heat - a._heat) || (a._dist - b._dist))
      .slice(0, 5);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, tripStartLatLng]);

  const toggle = (key) => {
    const n = new Set(selected);
    if (n.has(key)) n.delete(key); else n.add(key);
    setSelected(n);
  };

  const commit = () => {
    const picked = [];
    for (const key of selected) {
      const [kind, id] = key.split(":");
      if (kind === "lead") {
        const l = leads.find((x) => x.id === id);
        if (l) picked.push({
          lead_id: l.id,
          title: resolveLeadName(l),
          address: l.physical_address || [l.estate_reserve, l.nearest_town, l.province_area].filter(Boolean).join(", "),
          lat: l.lat ?? null,
          lng: l.lng ?? null,
          place_id: l.place_id || null,
          address_source: l.address_source || (l.lat && l.lng ? "places" : "manual"),
        });
      } else if (kind === "customer") {
        const c = customers.find((x) => x.id === id);
        if (c) picked.push({
          customer_id: c.id,
          title: c.name,
          address: c.physical_address || "",
        });
      }
    }
    onPick(picked);
  };

  const dotColor = (days) => {
    if (days == null) return "#647087";
    if (days < 14) return "#10B981";  // green
    if (days < 60) return "#F59E0B";  // amber
    return "#EF4444";                  // red
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="trip-stop-picker">
      <div onClick={(e) => e.stopPropagation()} className="hv-card max-w-4xl w-full max-h-[88vh] flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <MapPin className="w-5 h-5 text-[#1fb6c1]" />
          <h2 className="font-display text-lg font-bold">Pick stops for this trip</h2>
          <button type="button" onClick={onClose} className="ml-auto hv-btn hv-btn-ghost text-xs" data-testid="trip-picker-close">Close</button>
        </div>

        {/* Tabs + search */}
        <div className="p-3 border-b border-white/5 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white/5 rounded-md p-1">
            <button type="button" onClick={() => setTab("leads")} className={`text-xs px-3 py-1 rounded ${tab === "leads" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#a0aabf]"}`} data-testid="trip-picker-tab-leads">
              Leads ({filteredLeads.length})
            </button>
            <button type="button" onClick={() => setTab("customers")} className={`text-xs px-3 py-1 rounded ${tab === "customers" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#a0aabf]"}`} data-testid="trip-picker-tab-customers">
              Customers ({filteredCustomers.length})
            </button>
          </div>
          <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-white/5 rounded-md px-2 py-1">
            <Search className="w-3 h-3 text-[#647087]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name / area / industry…" className="flex-1 bg-transparent border-0 focus:outline-none text-xs" data-testid="trip-picker-search" />
          </div>
          {tab === "leads" && (
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="hv-input text-xs py-1" data-testid="trip-picker-sort">
              <option value="heat">Sort: Hot first</option>
              <option value="phase">Sort: Phase priority</option>
              <option value="recent">Sort: Last contact</option>
              <option value="az">Sort: A → Z</option>
              {tripStartLatLng && <option value="distance">Sort: Distance from start</option>}
            </select>
          )}
        </div>

        {/* Filter chips */}
        {tab === "leads" && (
          <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2 flex-wrap text-[10px]">
            <select
              value={filters.industry}
              onChange={(e) => setFilters((f) => ({ ...f, industry: e.target.value }))}
              className="hv-input text-[10px] py-0.5 px-1.5"
              data-testid="trip-picker-filter-industry"
            >
              <option value="">All industries</option>
              {allIndustries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <select
              value={filters.province}
              onChange={(e) => setFilters((f) => ({ ...f, province: e.target.value }))}
              className="hv-input text-[10px] py-0.5 px-1.5"
              data-testid="trip-picker-filter-province"
            >
              <option value="">All provinces</option>
              {allProvinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChipFilter
              label="🔥 Hot phase"
              active={filters.phaseHot}
              onClick={() => setFilters((f) => ({ ...f, phaseHot: !f.phaseHot }))}
              testId="trip-picker-filter-hot"
            />
            <ChipFilter
              label="5★"
              active={filters.has5Star}
              onClick={() => setFilters((f) => ({ ...f, has5Star: !f.has5Star }))}
              testId="trip-picker-filter-5star"
            />
            <ChipFilter
              label="Cold > 60d"
              active={filters.lastContactCold}
              onClick={() => setFilters((f) => ({ ...f, lastContactCold: !f.lastContactCold }))}
              testId="trip-picker-filter-cold"
            />
            {(filters.industry || filters.province || filters.phaseHot || filters.has5Star || filters.lastContactCold) && (
              <button
                type="button"
                onClick={() => setFilters({ industry: "", province: "", phaseHot: false, has5Star: false, lastContactCold: false })}
                className="text-[10px] text-[#647087] hover:text-[#1fb6c1] underline"
                data-testid="trip-picker-filter-clear"
              >Clear filters</button>
            )}
          </div>
        )}

        {/* Hidro suggestions banner */}
        {tab === "leads" && hidroSuggested.length > 0 && (
          <div className="px-3 py-2 border-b border-white/5 bg-[#1fb6c1]/5" data-testid="trip-picker-hidro-suggest">
            <div className="text-[10px] uppercase tracking-wider text-[#1fb6c1] flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3" />
              Hidro suggests — top 5 hot leads near your start point
            </div>
            <div className="flex flex-wrap gap-1.5">
              {hidroSuggested.map((l) => {
                const key = `lead:${l.id}`;
                const checked = selected.has(key);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggle(key)}
                    className={`text-[10px] px-2 py-1 rounded-full border ${checked ? "bg-[#1fb6c1] text-black border-[#1fb6c1]" : "border-[#1fb6c1]/40 text-[#1fb6c1] hover:bg-[#1fb6c1]/10"}`}
                    data-testid={`trip-picker-suggest-${l.id}`}
                  >
                    {checked ? "✓ " : "+ "}{resolveLeadName(l)} <span className="opacity-60">· {Math.round(l._dist)}km · {l._heat}°</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2">
          {tab === "leads" ? filteredLeads.map((l) => {
            const key = `lead:${l.id}`;
            const heat = computeRowHeat(l);
            const days = lastContactDays(l);
            const name = resolveLeadName(l);
            return (
              <label key={l.id} className="flex items-start gap-3 p-2.5 hover:bg-white/5 rounded cursor-pointer text-xs border-b border-white/5 last:border-0" data-testid={`trip-picker-lead-${l.id}`}>
                <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} className="accent-[#1fb6c1] mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-white">{name}</span>
                    {l.star_rating > 0 && (
                      <span className="text-[10px] text-amber-300 font-mono">{"★".repeat(Math.min(5, Number(l.star_rating)))}</span>
                    )}
                    {l.industry && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA] font-mono">{l.industry}</span>}
                    {l.phase && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1fb6c1]/15 text-[#1fb6c1] font-mono">{PHASE_LABEL[l.phase] || l.phase}</span>
                    )}
                    {l.address_source && l.address_source !== "manual" && l.address_source !== "legacy" && (
                      <span className="text-[9px] text-emerald-300" title={`Address verified (${l.address_source})`}>✓ verified</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#a0aabf] mt-0.5 flex items-center gap-2 flex-wrap">
                    {[l.estate_reserve, l.nearest_town, l.province_area].filter(Boolean).join(" · ") || "—"}
                    {days != null && (
                      <span className="flex items-center gap-1" style={{ color: dotColor(days) }} title={`Last contact ~${days}d ago`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor(days) }} />
                        {days}d
                      </span>
                    )}
                    <span className="ml-auto font-mono text-[#647087]">{heat}°</span>
                  </div>
                </div>
              </label>
            );
          }) : filteredCustomers.map((c) => {
            const key = `customer:${c.id}`;
            return (
              <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-white/5 rounded cursor-pointer text-xs" data-testid={`trip-picker-customer-${c.id}`}>
                <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} className="accent-[#1fb6c1]" />
                <Building2 className="w-3 h-3 text-[#A78BFA]" />
                <span className="font-bold">{c.name}</span>
                <span className="text-[#647087]">{c.industry || "—"} · {c.physical_address || "—"}</span>
              </label>
            );
          })}
          {tab === "leads" && filteredLeads.length === 0 && (
            <div className="p-8 text-center text-xs text-[#647087]">No leads match these filters.</div>
          )}
        </div>
        <div className="p-3 border-t border-white/10 flex items-center justify-between">
          <span className="text-xs text-[#a0aabf]">{selected.size} selected</span>
          <button type="button" onClick={commit} disabled={selected.size === 0} className="hv-btn hv-btn-primary text-sm" data-testid="trip-picker-add">
            <Plus className="w-3 h-3" /> Add {selected.size} stop{selected.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChipFilter({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-0.5 rounded-full border transition ${active ? "bg-[#1fb6c1]/15 border-[#1fb6c1] text-[#1fb6c1]" : "border-white/15 text-[#a0aabf] hover:border-white/30"}`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function ChecklistCard({ items, disabled, onAdd, onToggle, onRemove }) {
  const [text, setText] = useState("");
  const submit = (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    onAdd(t);
    setText("");
  };
  const total = items.length;
  const done = items.filter((i) => i.done).length;
  return (
    <div className="hv-card p-4" data-testid="trip-checklist">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Package className="w-5 h-5 text-[#A78BFA]" />
          Reminders & things to bring
          {total > 0 && <span className="text-xs font-mono text-[#647087]">({done}/{total})</span>}
        </h2>
      </div>
      {!disabled && (
        <form onSubmit={submit} className="flex items-center gap-2 mb-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Bring 5L water sample bottles · Print quote template · Top up fuel"
            className="hv-input text-sm flex-1"
            data-testid="trip-checklist-input"
          />
          <button type="submit" className="hv-btn hv-btn-primary text-xs" data-testid="trip-checklist-add">
            <Plus className="w-3 h-3" /> Add
          </button>
        </form>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-[#647087]">No reminders yet — add things you must prepare or take along on this trip.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-2 text-sm" data-testid={`trip-checklist-item-${it.id}`}>
              <button
                type="button"
                onClick={() => onToggle(it.id)}
                className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${it.done ? "bg-[#10B981] border-[#10B981]" : "border-white/30 hover:border-[#1fb6c1]"}`}
                disabled={disabled}
                aria-label={it.done ? "Mark not done" : "Mark done"}
              >
                {it.done && <Check className="w-3 h-3 text-black" />}
              </button>
              <span className={`flex-1 ${it.done ? "line-through text-[#647087]" : "text-[#d4dbe7]"}`}>{it.text}</span>
              {!disabled && (
                <button type="button" onClick={() => onRemove(it.id)} className="text-[#647087] hover:text-rose-300" aria-label="Remove">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* ─── Trip Command Centre tabs (Phase 2 + 3) ──────────────────────── */

/* Pure helpers shared by all tabs (mirror the cascade pattern from
   _cascadeArrivalTimes so we don't import that closed-over function). */
function _legMin(leg) {
  if (!leg) return null;
  if (leg.duration_min != null) return Number(leg.duration_min);
  if (leg.duration_s != null) return Number(leg.duration_s) / 60;
  return null;
}
function _hMin(m) {
  if (m == null || !Number.isFinite(Number(m))) return "—";
  const mn = Math.round(Number(m));
  if (mn < 60) return `${mn}m`;
  const h = Math.floor(mn / 60); const r = mn % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}
function _wa(num) {
  const cleaned = (num || "").replace(/[^\d+]/g, "");
  return cleaned ? `https://wa.me/${cleaned.replace(/^\+/, "")}` : null;
}
function _navUrl(addr, lat, lng, place_id) {
  if (place_id) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || "")}&query_place_id=${place_id}`;
  if (lat != null && lng != null) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  if (addr) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  return null;
}

function TripMapTab({ trip, googleMapsNavUrl }) {
  const stops = trip?.stops || [];
  const points = useMemo(() => {
    const arr = [];
    if (trip?.start_address || trip?.start_place_id) arr.push({
      label: trip.start_address, lat: trip.start_lat, lng: trip.start_lng,
      place_id: trip.start_place_id,
      kind: "depart", title: "Depart",
    });
    stops.forEach((s, i) => {
      if (s.address || s.lat != null || s.place_id) arr.push({
        label: s.address, lat: s.lat, lng: s.lng, place_id: s.place_id,
        kind: "stop", title: s.title || `Stop ${i + 1}`, idx: i + 1,
      });
    });
    return arr;
  }, [trip, stops]);

  const _legacyEmbedUrl = null; // Wave 25.17 — legacy Google Embed retired in favour of TripRouteMap

  // For the route to render we need ≥2 points with actual lat/lng — an
  // address alone (or a start with no stops) doesn't qualify.
  const mappable = points.filter((p) => p.lat != null && p.lng != null);
  const canRender = mappable.length >= 2;

  return (
    <div className="space-y-3" data-testid="trip-tab-map-content">
      <div className="hv-card p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-[#a0aabf]">
          <strong className="text-white">{mappable.length}</strong>{" "}
          of <strong className="text-white">{points.length}</strong> point{points.length === 1 ? "" : "s"} mapped.
          {!canRender && (
            <span className="text-amber-300">
              {" "}Add at least 2 stops with pins / autocomplete to see the route.
            </span>
          )}
        </div>
        {googleMapsNavUrl && (
          <a href={googleMapsNavUrl} target="_blank" rel="noreferrer" className="hv-btn hv-btn-primary text-xs" data-testid="trip-map-navigate">
            <Play className="w-3.5 h-3.5" /> Start navigation in Google Maps
          </a>
        )}
      </div>
      {canRender ? (
        <div className="hv-card overflow-hidden p-0 aspect-video sm:aspect-[16/9]">
          <TripRouteMap
            points={mappable}
            height="100%"
            className="border-0 rounded-none h-full"
          />
        </div>
      ) : (
        <div className="hv-card p-8 text-center text-xs text-[#647087]">
          Add at least two stops with addresses or pins to render the route map.
        </div>
      )}
      <div className="hv-card p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">Pin list</div>
        <ol className="space-y-2">
          {points.map((p, i) => {
            const nav = _navUrl(p.label, p.lat, p.lng, p.place_id);
            return (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1] text-[10px] font-mono font-bold flex items-center justify-center shrink-0">
                  {p.kind === "depart" ? "▶" : p.idx}
                </span>
                <span className="flex-1 truncate">{p.title} — <span className="text-[#a0aabf]">{p.label || "(no address)"}</span></span>
                {nav && <a href={nav} target="_blank" rel="noreferrer" className="hv-btn hv-btn-ghost text-[10px]"><MapPin className="w-3 h-3" /></a>}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function TripCalendarTab({ trip }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOther, setShowOther] = useState(false);
  useEffect(() => {
    if (!trip?.start_date) return;
    (async () => {
      setLoading(true);
      try {
        const start = trip.start_date;
        const end = trip.end_date || trip.start_date;
        const { data } = await api.get("/calendar/events", { params: { start, end } });
        setEvents((data || []).sort((a, b) => new Date(a.start) - new Date(b.start)));
      } catch (_e) { /* noop */ }
      setLoading(false);
    })();
  }, [trip?.start_date, trip?.end_date]);

  const tripEvents = events.filter((e) => e.trip_id === trip?.id);
  const otherEvents = events.filter((e) => !e.trip_id || e.trip_id !== trip?.id);

  return (
    <div className="space-y-3" data-testid="trip-tab-calendar-content">
      <div className="hv-card p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">This trip's meetings</div>
        {loading ? <div className="text-xs text-[#647087]">Loading…</div> : tripEvents.length === 0 ? (
          <div className="text-xs text-[#647087] italic">No meetings yet — activate the trip to push stops to the calendar.</div>
        ) : (
          <ul className="space-y-2" data-testid="trip-calendar-trip-meetings">
            {tripEvents.map((e) => (
              <li key={e.id} className="flex items-start gap-2 text-sm">
                <Calendar className="w-3.5 h-3.5 text-[#1FB6C1] mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{e.title}</div>
                  <div className="text-[10px] font-mono text-[#a0aabf]">{new Date(e.start).toLocaleString()}{e.end ? ` → ${new Date(e.end).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}` : ""}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {otherEvents.length > 0 && (
        <div className="hv-card p-3">
          <button
            type="button"
            onClick={() => setShowOther((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="trip-calendar-other-toggle"
          >
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Other meetings during this trip ({otherEvents.length})</div>
            <span className="text-xs text-[#1FB6C1]">{showOther ? "Hide" : "Show"}</span>
          </button>
          {showOther && (
            <>
              <ul className="space-y-2 mt-2" data-testid="trip-calendar-other-meetings">
                {otherEvents.map((e) => (
                  <li key={e.id} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-300 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate">{e.title}</div>
                      <div className="text-[10px] font-mono text-[#a0aabf]">{new Date(e.start).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="text-[10px] text-amber-300 mt-2 italic">⚠ These are meetings already on your calendar during this trip's window. Save will warn before any conflict.</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TripInsightsTab({ trip, driveTimes }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [loadingId, setLoadingId] = useState(null);
  const [insights, setInsights] = useState({});
  const stops = trip?.stops || [];

  const tightLegs = useMemo(() => {
    if (!driveTimes?.legs) return [];
    return driveTimes.legs.map((leg, i) => {
      const stop = stops[i];
      const driveMin = _legMin(leg);
      if (!stop || driveMin == null) return null;
      const dur = Number(stop.duration_min) || 60;
      // "Tight" = the previous stop's duration leaves <15 min slack vs drive
      if (i > 0) {
        const prev = stops[i - 1];
        const prevDur = Number(prev?.duration_min) || 60;
        if (prevDur < driveMin) {
          return { i, stop, leg, driveMin, prevDur, slack: prevDur - driveMin };
        }
      }
      void dur;
      return null;
    }).filter(Boolean);
  }, [driveTimes, stops]);

  const askHidro = async (kind, payload, key) => {
    setLoadingId(key);
    try {
      const sid = `trip-insight-${trip.id}-${key}`;
      const message = (
        kind === "tight" ?
          `My trip "${trip.title}" has tight scheduling on stop ${payload.idx} (${payload.title}). I have only ${payload.prevDur}m on-site at the previous stop but Google says drive is ${Math.round(payload.driveMin)}m. Suggest 2-3 concrete tactics to recover (call ahead, swap stops, adjust duration). Be terse.` :
        kind === "lodging" ?
          `My trip ends near "${payload.lastStop}" possibly after 19:00. List 3 affordable accommodation options within 25 km with rough nightly rates and direct-call numbers if you know them. If you're not sure, say so. Be brief.` :
        kind === "emergency" ?
          `List the standard emergency numbers a sales rep needs in South Africa (police, ambulance, tow service, roadside). Plus a tip on what to keep in the car.` :
          `Give one creative pre-trip tip for "${trip.title}" — something that turns a routine visit into a win.`
      );
      const { data } = await api.post("/hidro/chat", {
        session_id: sid,
        message,
      }, { timeout: 60_000 });
      setInsights((p) => ({ ...p, [key]: data.reply || "_(no reply)_" }));
    } catch (e) {
      setInsights((p) => ({ ...p, [key]: `_Failed: ${e?.message || `ask ${aiName} again`}_` }));
    }
    setLoadingId(null);
  };

  const lastStop = stops[stops.length - 1];

  return (
    <div className="space-y-3" data-testid="trip-tab-insights-content">
      <div className="hv-card p-3 border-[#1FB6C1]/30 bg-gradient-to-br from-[#1FB6C1]/5 to-transparent">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#1FB6C1]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] font-semibold">{aiName} AI Insights</div>
        </div>
        <p className="text-xs text-[#a0aabf]">
          On-demand pre-trip intelligence. Click any card below to ask {aiName} to analyse this specific trip.
          Keeps the rep ahead of issues without auto-burning LLM credits.
        </p>
      </div>

      {/* Tight schedule warnings */}
      {tightLegs.length > 0 && (
        <div className="hv-card p-3 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <div className="text-sm font-medium">Tight scheduling detected</div>
          </div>
          {tightLegs.map((t) => {
            const key = `tight-${t.i}`;
            return (
              <div key={key} className="border border-amber-500/20 rounded-md p-2 mb-2 last:mb-0">
                <div className="text-xs">
                  <strong className="text-amber-200">Stop {t.i + 1}: {t.stop.title}</strong> — only {t.prevDur}m on-site at previous stop, but drive is {Math.round(t.driveMin)}m.
                </div>
                <button
                  type="button"
                  onClick={() => askHidro("tight", { idx: t.i + 1, title: t.stop.title, prevDur: t.prevDur, driveMin: t.driveMin }, key)}
                  disabled={loadingId === key}
                  className="hv-btn hv-btn-ghost text-[10px] mt-1"
                  data-testid={`insight-tight-${t.i}`}
                >
                  {loadingId === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {loadingId === key ? "Analysing…" : `Ask ${aiName} for tactics`}
                </button>
                {insights[key] && (
                  <div className="mt-2 border-t border-amber-500/15 pt-2">
                    <HidroMarkdown text={insights[key]} className="text-xs text-amber-100" compact />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lodging suggestion */}
      <InsightCard
        title="Affordable lodging near final stop"
        desc={lastStop ? `If you wrap stop "${lastStop.title}" late, sleep nearby instead of driving home.` : "Add stops first."}
        icon={<MapPin className="w-4 h-4 text-emerald-400" />}
        disabled={!lastStop}
        loading={loadingId === "lodging"}
        onClick={() => askHidro("lodging", { lastStop: lastStop?.title || "" }, "lodging")}
        result={insights.lodging}
        testId="insight-lodging"
      />

      {/* Emergency numbers */}
      <InsightCard
        title="Emergency contacts for the road"
        desc="Police, ambulance, tow, and a 'what to keep in the car' tip."
        icon={<Phone className="w-4 h-4 text-rose-400" />}
        loading={loadingId === "emergency"}
        onClick={() => askHidro("emergency", {}, "emergency")}
        result={insights.emergency}
        testId="insight-emergency"
      />

      {/* Pre-trip tip */}
      <InsightCard
        title="Pre-trip tip"
        desc="One creative tactic to make this trip count."
        icon={<Sparkles className="w-4 h-4 text-purple-400" />}
        loading={loadingId === "tip"}
        onClick={() => askHidro("tip", {}, "tip")}
        result={insights.tip}
        testId="insight-tip"
      />
    </div>
  );
}

function InsightCard({ title, desc, icon, disabled, loading, onClick, result, testId }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  return (
    <div className="hv-card p-3" data-testid={testId}>
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-[11px] text-[#a0aabf] mt-0.5">{desc}</div>
        </div>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled || loading}
          className="hv-btn hv-btn-ghost text-[10px] shrink-0"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {loading ? "Asking…" : `Ask ${aiName}`}
        </button>
      </div>
      {result && (
        <div className="mt-2 border-t border-white/5 pt-2">
          <HidroMarkdown text={result} className="text-xs text-[#a0aabf]" compact />
        </div>
      )}
    </div>
  );
}

function TripItineraryTab({ trip, driveTimes, googleMapsNavUrl }) {
  const stops = trip?.stops || [];
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({}); // { [stopId]: bool }
  const [savingNotes, setSavingNotes] = useState("");

  useEffect(() => {
    if (!trip?.id) return;
    setLoading(true);
    api.get(`/trips/${trip.id}/itinerary-pack`)
      .then((r) => setPack(r.data))
      .catch(() => setPack(null))
      .finally(() => setLoading(false));
  }, [trip?.id, trip?.updated_at]);

  // Auto-generate any missing brief on entry to this tab. Sequential to
  // avoid hammering the LLM. Notify user when ready.
  useEffect(() => {
    if (!pack?.stops) return;
    const missing = pack.stops.filter((s) => s.lead_id && !s.sales_brief_doc?.body && !generating[s.id]);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      for (const s of missing) {
        if (cancelled) return;
        setGenerating((g) => ({ ...g, [s.id]: true }));
        try {
          await api.post(`/trips/${trip.id}/stops/${s.id}/generate-brief`);
          // Refresh just this stop's brief
          try {
            const { data: latest } = await api.get(`/trips/${trip.id}/itinerary-pack`);
            if (!cancelled) setPack(latest);
          } catch (_e) { /* noop */ }
        } catch (_e) { /* noop */ }
        setGenerating((g) => ({ ...g, [s.id]: false }));
      }
      // Final notify
      if (!cancelled && missing.length > 0) {
        try {
          window.dispatchEvent(new CustomEvent("hv:toast", { detail: {
            level: "success",
            title: "Sales Prep Pack ready",
            body: `${missing.length} brief${missing.length === 1 ? "" : "s"} generated. Print is ready.`,
          }}));
        } catch (_e) { /* noop */ }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack?.stops?.length]);

  const totalStopMin = stops.reduce((acc, s) => acc + (Number(s.duration_min) || 60), 0);
  const totalDriveMin = driveTimes?.totals?.duration_min ?? (driveTimes?.totals?.duration_s != null ? Math.round(driveTimes.totals.duration_s / 60) : 0);
  const totalAll = totalDriveMin + totalStopMin;

  const saveStopNotes = async (stopId, notes) => {
    setSavingNotes(stopId);
    try {
      await api.put(`/trips/${trip.id}/stops/${stopId}/notes`, { notes_after_visit: notes });
    } catch (_e) { /* noop */ }
    setSavingNotes("");
  };

  const onPrint = () => {
    // Add a body class so our print CSS only triggers for THIS view, not
    // anything else (the trip-level @media print used to leak to global pages).
    document.body.classList.add("hv-print-itinerary");
    // Wave 19 #100 — Print Light Mode Contract: force light theme for
    // the duration of the print and restore on afterprint.
    printInLightMode({
      delay: 100,
      onAfterPrint: () => document.body.classList.remove("hv-print-itinerary"),
    });
  };

  // Wave 19 #102 — server-side Playwright PDF download.
  const tripShortId = trip?.short_id || trip?.id?.slice(0, 8) || "trip";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: trip?.id ? `/trips/${trip.id}/pdf` : null,
    filename: `Trip_${tripShortId}.pdf`,
  });

  return (
    <div className="space-y-4" data-testid="trip-tab-itinerary-content">
      {/* Wave 19 #103 — Deep Itinerary panel sits above the legacy
          Sales Prep Pack so the rep sees the long-form portfolio doc
          first when Deep Mode is active. */}
      <DeepItineraryPanel trip={trip} />

      <div className="hv-card p-3 flex items-center justify-between flex-wrap gap-2 print:hidden">
        <div className="text-xs text-[#a0aabf]">
          Multi-page Sales Prep Pack — printable as PDF. {Object.values(generating).some(Boolean) ? <span className="text-amber-300">Generating briefs…</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="trip-itinerary-download-pdf"
            title="Download a server-rendered PDF — always light mode, deterministic layout"
          >
            <Download className="w-4 h-4" />
            {downloadingPdf ? "Generating…" : "Download PDF"}
          </button>
          <button
            type="button"
            onClick={onPrint}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="trip-itinerary-print"
            title="Print in browser — always renders in light mode"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      {loading && <div className="hv-card p-6 text-center text-xs text-[#647087]">Loading prep pack…</div>}

      {!loading && pack && (
        <div className="trip-prep-pack space-y-4" data-testid="trip-itinerary-doc">
          {/* ── PAGE 1: COVER ─────────────────────────────────────── */}
          <section className="hv-card p-6 print:p-0 print:border-0 print:shadow-none print:break-after-page" data-testid="trip-prep-cover">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1]">Sales Prep Pack</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{trip.title}</h1>
            <div className="text-xs text-[#a0aabf] font-mono mt-2">
              {trip.start_date}{trip.start_time ? ` · depart ${trip.start_time}` : ""}
              {trip.end_date && trip.end_date !== trip.start_date ? ` → ${trip.end_date}` : ""}
            </div>
            {trip.start_address && <div className="text-sm mt-2">From <strong>{trip.start_address}</strong></div>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
              <Stat label="Stops" value={stops.length} />
              <Stat label="Drive" value={_hMin(totalDriveMin)} />
              <Stat label="On-site" value={_hMin(totalStopMin)} />
              <Stat label="Total" value={_hMin(totalAll)} />
            </div>
            {trip.notes && (
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Trip notes</div>
                <p className="text-sm whitespace-pre-wrap">{trip.notes}</p>
              </div>
            )}
            {(trip.checklist || []).length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Checklist</div>
                <ul className="text-sm space-y-1">
                  {(trip.checklist || []).map((c, i) => (
                    <li key={c.id || i} className="flex items-center gap-2">
                      <span className="w-4 h-4 border border-white/30 rounded inline-flex items-center justify-center">{c.done ? <Check className="w-3 h-3 text-[#1FB6C1]" /> : null}</span>
                      <span>{c.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ol className="mt-4 pt-3 border-t border-white/10 space-y-2">
              <li className="text-xs flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1] text-[10px] font-mono font-bold flex items-center justify-center shrink-0">▶</span><span>Depart {trip.start_time || ""} from {trip.start_address || "—"}</span></li>
              {pack.stops.map((s, i) => (
                <li key={s.id} className="text-xs flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-white/5 border border-white/15 text-[10px] font-mono font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 truncate"><strong>{s.title || `Stop ${i + 1}`}</strong> · {s.arrival_time ? new Date(s.arrival_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} · {s.address || ""}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* ── PAGES 2..N: ONE FULL PAGE PER STOP ────────────────── */}
          {pack.stops.map((s, i) => {
            const wa = _wa(s.contact_phone);
            const nav = _navUrl(s.address, s.lat, s.lng, s.place_id);
            const brief = s.sales_brief_doc;
            const lead = s.lead;
            return (
              <section key={s.id} className="hv-card p-6 print:p-0 print:border-0 print:shadow-none print:break-after-page space-y-4" data-testid={`trip-prep-stop-${s.id}`}>
                <header className="flex items-start gap-3 pb-3 border-b border-white/10">
                  <span className="w-9 h-9 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1] text-sm font-mono font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-2xl font-bold">{s.title || `Stop ${i + 1}`}</h2>
                    <div className="text-xs text-[#a0aabf]">{s.address || "(no address)"}</div>
                    <div className="text-xs font-mono mt-1">
                      <span className="text-[#1FB6C1]">{s.arrival_time ? new Date(s.arrival_time).toLocaleString() : "—"}</span>
                      <span className="text-[#a0aabf]"> · {_hMin(s.duration_min || 60)} on-site</span>
                    </div>
                  </div>
                </header>

                {/* Lead snapshot */}
                {lead && (
                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <KV label="Star rating" value={lead.star_rating || lead.official_star_rating || lead.website_star_rating || "—"} />
                    <KV label="Price / night" value={lead.price_per_night || "—"} />
                    <KV label="Rooms" value={lead.rooms_count || "—"} />
                    <KV label="Phone" value={lead.own_contact_phone || s.contact_phone || "—"} />
                    <KV label="Website" value={lead.website || "—"} />
                    <KV label="Established" value={lead.established_year || "—"} />
                  </div>
                )}

                {/* Photos strip */}
                {s.photos?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Photos</div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                      {s.photos.slice(0, 6).map((p) => (
                        <img
                          key={p.file_url}
                          src={uploadUrl(p.file_url) || p.file_url}
                          alt=""
                          className="w-full h-16 object-cover rounded border border-white/5"
                          loading="lazy"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Pre-meeting prep — discussion points + brief */}
                {s.discussion_points && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Agenda / discussion points</div>
                    <p className="text-sm whitespace-pre-wrap">{s.discussion_points}</p>
                  </div>
                )}

                {brief?.body ? (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Sales prep brief</div>
                    <HidroMarkdown text={brief.body} className="text-sm" />
                    {brief.checklist?.length > 0 && (
                      <ul className="mt-2 text-xs space-y-1">
                        {brief.checklist.map((c, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 border border-white/30 rounded inline-flex items-center justify-center">{c.done ? <Check className="w-2 h-2 text-[#1FB6C1]" /> : null}</span>
                            <span>{c.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : generating[s.id] ? (
                  <div className="text-xs text-amber-300 italic"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Generating sales brief…</div>
                ) : s.lead_id ? (
                  <div className="text-xs text-[#647087] italic">Sales brief pending — auto-generates on first print.</div>
                ) : null}

                {/* Contacts */}
                {s.contacts?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Contacts at this stop</div>
                    <ul className="text-xs space-y-1">
                      {s.contacts.map((c) => (
                        <li key={c.id} className="flex items-center gap-2 flex-wrap">
                          <strong>{c.name}</strong>
                          {c.role && <span className="text-[#A78BFA]">{c.role}</span>}
                          {c.phone && <a href={`tel:${c.phone}`} className="text-[#1FB6C1]">{c.phone}</a>}
                          {c.email && <a href={`mailto:${c.email}`} className="text-[#1FB6C1]">{c.email}</a>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recent activity */}
                {s.recent_calls?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Recent activity</div>
                    <ul className="text-xs space-y-1">
                      {s.recent_calls.slice(0, 3).map((c, idx) => (
                        <li key={idx} className="text-[#a0aabf]">
                          <span className="font-mono">{c.created_at?.slice(0, 10)}</span> · {c.duration_min || 0}m · {c.notes?.slice(0, 80) || "(no notes)"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Quick links */}
                <div className="text-xs flex flex-wrap items-center gap-3 pt-2 border-t border-white/5 print:no-underline">
                  {s.contact_phone && <a href={`tel:${s.contact_phone}`} className="text-[#1FB6C1] underline">Call {s.contact_name || s.contact_phone}</a>}
                  {wa && <a href={wa} target="_blank" rel="noreferrer" className="text-emerald-300 underline">WhatsApp</a>}
                  {nav && <a href={nav} target="_blank" rel="noreferrer" className="text-purple-300 underline">Navigate</a>}
                  {lead?.website && <a href={lead.website} target="_blank" rel="noreferrer" className="text-[#1FB6C1] underline">{lead.website.replace(/^https?:\/\//, "").slice(0, 40)}</a>}
                </div>

                {/* Notes after visit */}
                <NotesAfterVisit
                  initial={s.notes_after_visit || ""}
                  onSave={(v) => saveStopNotes(s.id, v)}
                  saving={savingNotes === s.id}
                  testId={`trip-prep-notes-${s.id}`}
                />
              </section>
            );
          })}

          {/* ── FINAL PAGE: EMERGENCY + DIRECTORY ──────────────── */}
          <section className="hv-card p-6 print:p-0 print:border-0 print:shadow-none" data-testid="trip-prep-emergency">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300">Emergency · in case of need</div>
            <h2 className="font-display text-xl font-bold mt-1">Emergency numbers</h2>
            <table className="text-sm mt-3 w-full">
              <tbody>
                {(pack.emergency_numbers || []).map((e) => (
                  <tr key={e.label} className="border-b border-white/5">
                    <td className="py-1 pr-3 text-[#a0aabf]">{e.label}</td>
                    <td className="py-1 font-mono"><a href={`tel:${e.number.replace(/\s/g, "")}`} className="text-[#1FB6C1] no-underline">{e.number}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Full contact directory</div>
              <ul className="text-xs space-y-1">
                {pack.stops.flatMap((s) => (s.contacts || []).map((c) => ({...c, _stop: s.title}))).map((c, idx) => (
                  <li key={idx} className="flex items-center gap-2 flex-wrap">
                    <strong>{c.name}</strong>
                    <span className="text-[#A78BFA]">{c._stop}</span>
                    {c.phone && <a href={`tel:${c.phone}`} className="text-[#1FB6C1]">{c.phone}</a>}
                    {c.email && <a href={`mailto:${c.email}`} className="text-[#1FB6C1]">{c.email}</a>}
                  </li>
                ))}
              </ul>
            </div>

            <footer className="pt-4 mt-6 border-t border-white/10 text-[10px] text-[#647087]">
              Generated {new Date(pack.generated_at || Date.now()).toLocaleString()} by {pack.generated_by || "—"}
              {googleMapsNavUrl && <> · <a href={googleMapsNavUrl} target="_blank" rel="noreferrer" className="text-[#1FB6C1] underline">Live navigation</a></>}
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded border border-white/10 px-2 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="font-display text-base font-bold mt-0.5">{value}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="text-xs">
      <span className="text-[#647087]">{label}: </span>
      <span className="font-medium">{String(value ?? "—")}</span>
    </div>
  );
}

function NotesAfterVisit({ initial, onSave, saving, testId }) {
  const [val, setVal] = useState(initial || "");
  const [dirty, setDirty] = useState(false);
  return (
    <div className="pt-3 border-t border-white/5">
      <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1 flex items-center justify-between">
        <span>Notes after visit (capture in car / on the print-out)</span>
        {dirty && (
          <button type="button" onClick={() => { onSave(val); setDirty(false); }} className="text-[10px] text-[#1FB6C1]" data-testid={`${testId}-save`} disabled={saving}>
            {saving ? "Saving…" : "Save notes"}
          </button>
        )}
      </div>
      {/* On-screen: editable textarea. On print: lined writing space + any saved notes pre-rendered. */}
      <div className="print:hidden">
        <VoiceTextarea
          value={val}
          onChange={(v) => { setVal(v); setDirty(true); }}
          rows={4}
          placeholder="What did the prospect say? Next-step commitment? Pricing reaction? … Or tap the mic."
          className="text-sm w-full"
          context="meeting_outcome"
          testIdPrefix={`${testId}-voice`}
          data-testid={testId}
        />
      </div>
      <div className="hidden print:block">
        {val && <div className="text-sm whitespace-pre-wrap mb-2">{val}</div>}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} className="border-b border-black/30 h-5" />
          ))}
        </div>
      </div>
    </div>
  );
}
